// ---------------------------------------------------------------------------
// CandleSource: de onde vêm os blocos de velas, com resultado TIPADO.
//
// Hoje o app trata qualquer falha de carga como "fim do histórico". Com o resultado tipado, quem chama distingue:
//   ok            : velas recebidas (pode ser um bloco parcial);
//   vazio         : a fonte respondeu sem nenhuma vela;
//   fimDosDados   : a fonte respondeu, mas nada mais antigo que o pedido (o começo do mercado);
//   erro          : falhou, com o TIPO: rede | limite (429) | bloqueio (451) | fonteTrocada | outro.
// Só `vazio` e `fimDosDados` significam fim do histórico.
//
// "Nada pode assumir a Binance": a interface só fala de símbolo, timeframe e horários. A ÚNICA implementação no F1
// (`BinanceCandleSource`) envolve o caminho atual (`fetchRawBinanceData`) SEM mudar seu comportamento; os ativos
// tradicionais (Yahoo) continuam passando pelo mesmo caminho atual, sem mudanças (F6).
// ---------------------------------------------------------------------------

import { fetchRawBinanceData, candleSourceOf } from './BinanceService';
import type { RawCandle } from './seriesWindow';

/**
 * Velas por pedido que a fonte aceita (Binance, GET /api/v3/klines: limit máx. 1.000). A troca de série (F3d) monta
 * seus pedidos com este tamanho (seriesAtMoment.GRID_CANDLES); outra fonte (Tiingo) muda só esta constante.
 */
export const MAX_CANDLES_PER_REQUEST = 1000;

export type CandleErrorKind = 'rede' | 'limite' | 'bloqueio' | 'fonteTrocada' | 'outro';

export type FetchBlockResult =
  | { kind: 'ok'; candles: RawCandle[]; source: string | null }
  | { kind: 'vazio' }
  | { kind: 'fimDosDados' }
  | { kind: 'erro'; tipo: CandleErrorKind; status: number | null; message: string };

export interface FetchBlockRequest {
  symbol: string;
  /** intervalo da fonte ('1m', '1h', '1d'...) */
  interval: string;
  /** buscar velas com openTime ESTRITAMENTE ANTERIOR a este horário (carga de histórico); ausente = as mais novas */
  before?: number;
  /** quantas velas */
  limit: number;
}

export interface CandleSource {
  /** nome curto para o log */
  readonly name: string;
  fetchBlock(req: FetchBlockRequest): Promise<FetchBlockResult>;
}

/** Fontes aceitas como "a mesma corretora" (a Binance e o seu espelho de dados públicos). Outra = fonte trocada. */
export const BINANCE_HOSTS = ['data-api.binance.vision', 'api.binance.com'];

/** Classifica uma falha da busca (erro lançado por fetchRawBinanceData ou equivalente). Puro (testável). */
export function classifyFetchError(err: any): { tipo: CandleErrorKind; status: number | null; message: string } {
  const message = String(err?.message ?? err ?? 'erro').slice(0, 160);
  const status: number | null = typeof err?.status === 'number' ? err.status : null;
  const upstream = String(err?.upstream ?? '');
  // sem resposta HTTP: falha de rede ou tempo esgotado (AbortController de 15 s)
  if (status === null) {
    if (err?.name === 'AbortError' || err instanceof TypeError || /failed to fetch|network|load failed|abort/i.test(message)) return { tipo: 'rede', status, message };
    return { tipo: 'outro', status, message };
  }
  if (status === 429 || /(^|,)429(,|$)/.test(upstream)) return { tipo: 'limite', status, message };
  // F3e C2: "bloqueio" só com 451/403 de verdade: o status final, ou o espelho PRINCIPAL (o 1º) bloqueou e nenhum
  // respondeu outra coisa (sem resposta, 0, não conta). Antes, qualquer 451 na lista (api.binance.com bloqueia por
  // região) virava "bloqueio", mesmo com o principal fora do ar ou com pedido inválido (400).
  const ups = upstream ? upstream.split(',').map(Number) : [];
  if (status === 451 || status === 403 || (ups.length > 0 && (ups[0] === 451 || ups[0] === 403) && ups.every(n => n === 0 || n === 451 || n === 403))) return { tipo: 'bloqueio', status, message };
  if (status >= 500 || status === 408) return { tipo: 'rede', status, message };
  return { tipo: 'outro', status, message };
}

/** A resposta veio de outra corretora? (cabeçalho do servidor; ausente = desconhecido = aceito, como hoje) */
export function isSourceSwapped(source: string | null, accepted: readonly string[] = BINANCE_HOSTS): boolean {
  if (!source) return false;
  return source.split(',').map(s => s.trim()).filter(Boolean).some(h => !accepted.includes(h));
}

/** Monta o resultado de um bloco "antes de T" a partir da resposta crua (puro: testável). */
export function blockResult(rows: RawCandle[] | null | undefined, before: number | undefined, source: string | null): FetchBlockResult {
  if (isSourceSwapped(source)) return { kind: 'erro', tipo: 'fonteTrocada', status: null, message: `resposta de outra fonte (${source})` };
  if (!rows || !Array.isArray(rows) || rows.length === 0) return { kind: 'vazio' };
  const candles = before === undefined ? rows : rows.filter(k => k[0] < before);
  if (candles.length === 0) return { kind: 'fimDosDados' };
  return { kind: 'ok', candles, source };
}

/** A única implementação do F1: o caminho atual (proxy /api/binance; tradicionais pelo Yahoo, como hoje). */
export class BinanceCandleSource implements CandleSource {
  readonly name = 'binance';
  constructor(private readonly fetcher: typeof fetchRawBinanceData = fetchRawBinanceData) {}
  async fetchBlock(req: FetchBlockRequest): Promise<FetchBlockResult> {
    let rows: any[] | null;
    try {
      // o pedido de histórico de hoje usa endTime = openTime da vela mais antiga (inclusivo) e filtra as < endTime
      rows = await this.fetcher(req.symbol, req.interval, req.limit, req.before);
    } catch (err) {
      return { kind: 'erro', ...classifyFetchError(err) };
    }
    return blockResult(rows, req.before, rows ? candleSourceOf(rows) : null);
  }
}
